// ── TracedErrorBoundary ──────────────────────────────────────────────────────
// Error boundary that creates an OTEL span + captures to Sentry on error.
// Wraps React's error boundary pattern with observability hooks.

import React from 'react'
import { trace, SpanStatusCode } from '@opentelemetry/api'

import { captureError } from '../sentry/init'

/** Render-prop form of the fallback — receives the error + a reset callback. */
type FallbackRender = (props: { error: Error | null; resetError: () => void }) => React.ReactNode

type TracedErrorBoundaryProps = {
  mfeName: string
  /** Fallback UI: a static node, or a render-prop receiving { error, resetError }. */
  fallback: React.ReactNode | FallbackRender
  children: React.ReactNode
}

type TracedErrorBoundaryState = {
  hasError: boolean
  error: Error | null
}

export class TracedErrorBoundary extends React.Component<
  TracedErrorBoundaryProps,
  TracedErrorBoundaryState
> {
  // CLASS COMPONENT CONTEXT DECLARATION intentionally — React 19 @types/react
  // requires explicit context member for JSX element type checking (TS2607/TS2786).
  // Ref: ASC-5276
  declare context: unknown

  constructor(props: TracedErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): TracedErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { mfeName } = this.props

    // Create OTEL span for the error.
    try {
      const tracer = trace.getTracer(`@platform/observezen-react/${mfeName}`)
      const span = tracer.startSpan('react_error_boundary', {
        attributes: {
          'react.component': mfeName,
          'error.type': error.name,
          'error.message': error.message,
          'react.componentStack': errorInfo.componentStack?.slice(0, 500) ?? '',
        },
      })
      span.setStatus({ code: SpanStatusCode.ERROR, message: error.message })
      span.recordException(error)
      span.end()
    } catch {
      // OTEL not available — skip span creation.
    }

    // Capture to Sentry via the adapter (no direct vendor import here).
    captureError(error)
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props
      return typeof fallback === 'function'
        ? fallback({ error: this.state.error, resetError: this.resetError })
        : fallback
    }
    return this.props.children
  }
}
